import { AgAuthenticationService, AuthenticationConfig } from '@ag/vc.ag-core/authentication';
import { AgAppConfigService } from '@ag/vc.ag-core/settings';
import { EventEmitter, Injectable } from '@angular/core';
import { Params, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { OnlinePaymentEntryPage, OnlinePaymentInfo } from 'src/app/shared/models/Contract';
import { PromoValidatedData } from 'src/app/shared/models/PromoCodeModel';
import { BrokerInfo } from '../../shared/models/BrokerInfo';
import { CaptureChildDetails } from '../../shared/models/CaptureChildDetails';
import { DeepLinkingModel } from '../../shared/models/DeeplinkParams';
import { ResourceModel } from '../../shared/models/ResourceModel';
import { BeneficiaryChild } from '../../shared/models/SurveyModel';
import { Brand } from '../../shared/models/brand';
import { DistributionChannel } from '../../shared/models/distributionChannelType';
import { SimulationParam } from '../../shared/models/simulationParams';
import { HelperService } from '../../shared/services/helper.service';
import { Context, ProcessCd } from '../models/context';
import { Session } from '../models/session';
import { SimulationData } from '../models/simulationData';
import { ValidationConstants } from '../models/validationconstants';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private session: Session;
  private sessionKey = 'userSession';
  context = new EventEmitter<Context>();
  // public isNewUserFlow = false;
  private isHelpWidgetRequired = new BehaviorSubject<boolean>(true);
  public isUserBlocked = false;
  private maxDuration = 7200000; // max duration for the session in milliseconds;
  private storage: Storage;
  public maxDurationToStartSurvey = 3600000;
  private isAuthenticated = new BehaviorSubject<boolean>(false);
  private isRedirectedToUPScreen = new BehaviorSubject<boolean>(false);
  private refreshDynamicHeader = new Subject<boolean>();
  private paramsLoaded = new Subject<boolean>();
  private utmParameters = ['utm_medium', 'utm_source', 'utm_campaign', 'utm_content', 'utm_term'];
  constructor(
        private readonly helper: HelperService,        
        private readonly authenticationService: AgAuthenticationService,
        private readonly router: Router
  ) {
    // this.storage = sessionStorage;
    this.storage = localStorage;
    this.getSession();
    this.initNewSession();
  }

  private initNewSession(): void {
    if (!this.session) {
      this.session = new Session();
    }

    if (!this.session.context) {
      this.session.context = new Context();
    }

    if (!this.session.simulationData) {
      this.session.simulationData = new SimulationData();
    }
    if (!this.session.ValidationConstants){
      this.session.ValidationConstants = new ValidationConstants();
    }
    if (!this.session.userParam) {
      this.session.userParam = {} as SimulationParam;
      this.session.userParam.language = 'fr';
    }
    if (!this.session.utm) {
      this.session.utm = {};
    }
    if (!this.session.PartnerInfo) {
      this.session.PartnerInfo = {IsPlanDistributor: null, Partner: null};
    }
    if (!this.session.beneficiaryChild) {
      this.session.beneficiaryChild = new BeneficiaryChild();
    }
    if (!this.session.resource) {
      this.session.resource = new Array<ResourceModel>();
    }
    if (!this.session.childDetails) {
      this.session.childDetails = new Array<CaptureChildDetails>();
    }
  }

  public clearSession(): void {
    this.storage.removeItem(this.sessionKey);
    this.session = null;
    this.initNewSession();
  }


  private getSession(): void {
    this.session = JSON.parse(this.storage.getItem(this.sessionKey)) as Session;

    if (this.session && this.session.timestamp && (this.getCurrentTimeStamp() - this.session.timestamp > this.maxDuration)) {
      this.clearSession();
    }

    if (this.session && this.session.userParam && this.session.userParam.distributionChannel === DistributionChannel.Broker){
      this.setHelpWidgetSubject(true);
    }
  }

  private saveSession(): void {
    this.session.timestamp = this.getCurrentTimeStamp();
    this.storage.setItem(this.sessionKey, JSON.stringify(this.session));
  }

  private getCurrentTimeStamp(): number {
    return new Date().getTime();
  }

  public setContext(brand: Brand, coBrandLogo: string, brokerLogo = '', brokerWebsiteUrl = '', isBrandClickable = true, partnerUrlFr?: string, partnerUrlNl?: string): void {
    this.session.context.brand = brand;
    this.session.context.coBrandLogo = coBrandLogo;
    this.session.context.brokerLogo = brokerLogo;
    // not sure whether the broker's website url will contain the 'https' or not, so taking care of it in the template
    this.session.context.brokerWebsiteUrl = brokerWebsiteUrl ?
      (brokerWebsiteUrl.includes('https://') ? brokerWebsiteUrl.substring(8) : brokerWebsiteUrl) :
      null;
    this.session.context.isBrandClickable = isBrandClickable;
    this.session.context.partnerUrlFr = partnerUrlFr ? (partnerUrlFr.includes('https://') ? partnerUrlFr.substring(8) : partnerUrlFr) : null;
    this.session.context.partnerUrlNl = partnerUrlNl ? (partnerUrlNl.includes('https://') ? partnerUrlNl.substring(8) : partnerUrlNl) : null;
    this.context.emit(this.getContext());
    this.saveSession();
  }

  public getContext(): Context {
    return this.session.context;
  }

  public setUserLanguage(lang: string): void {
    this.session.userParam.language = lang;
    this.saveSession();
  }

  public getContextPrefix(): string {
    if (this.session && this.session.context && this.session.context.brand === Brand.Uppie) {
      return 'uppie';
    } else {
      return 'sil';
    }
  }

  public setUtmParams(params: Params): void {
    this.clearUtmParams();
    let utm_params_analytics ='';
    this.utmParameters.forEach((utm, i:number) => {
      const value = params[utm] as string;

      if (value) {
        this.session.utm[utm] = value;
        utm_params_analytics += `${i===0?'':'&'}${utm}=${value}`;
      }
    });
    this.storage.utm_parameters = utm_params_analytics;
    this.saveSession();
  }

  public getUtmParams(): {[key: string]: string} {
    return this.session.utm;
  }

  public clearUtmParams(): void {
    this.session.utm = {};
    this.saveSession();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  public setUserParams(params: Params): void { // storing the context for the rest of the flow, as per US-27691
    const getUserParams = this.getUserParams().language;
    this.session.userParam = {
      language: params.lang ? String(params.lang).toLowerCase() : getUserParams,
      distributionChannel: Number(params.distcha),
      partnercode: params.partcd as string|number,
      partnercodeRange: Number(params.partcdRange),
      simulationDate: <string>params.simdate,
      duration: Number(params.duration),
      monthlyAmount: Number(params.mthamt),
      oneShortAmount: Number(params.osamt),
      clientName: <string>params.clientname,
      isFromEmail: Boolean(params.fmemail),
      clientExtRef: params.clientextref ? decodeURIComponent(<string>params.clientextref) : '',
      ProcessCd: params.ProcessCd as string
    } as SimulationParam;
    if (params.simdate){
      this.prefillSimulationData(String(params.simdate), Number(params.duration), Number(params.osamt), Number(params.mthamt));
    }
    // if (params.partcd){
    //   // Incase alters the partner code while starting the new survey, the flag for continuation of survey is removed
    //   this.helper.clearSession(SessionKeys.startSurveyKey);
    // }
    this.setBrokerDetails(null);
    setTimeout(() => this.setContext(null, null)); // setimeout is added to overcome 'ExpressionChangedAfterItHasBeenCheckedError' issue
    // this.isNewUserFlow = true; // flow is new when user introduces the parameters in the url
    this.saveSession();
    this.refreshParams(true);
  }

  public setModifiedUserParam(params: SimulationParam): void {
    this.session.userParam = params;
    this.saveSession();
    this.refreshParams(true);
  }

  public getUserParams(): SimulationParam {
    return this.session.userParam;
  }

  // setting the header context as per US-27689
  public setHeaderContext(brokerLogo: string, brokerWebsiteUrl?: string, isFromBrokerInfo?: boolean, partnerImage?: string, partnerLinkFr?: string, partnerLinkNl?: string): void {
    if (!(this.session.userParam.distributionChannel === DistributionChannel.AG
            || this.session.userParam.distributionChannel === DistributionChannel.Broker)){
      this.setContext(Brand.AG, '');
    } else {
      if (this.session.userParam.distributionChannel === DistributionChannel.AG
                && this.session.userParam.partnercode){
        // TODO : fetch the partner logo as per the the partner code, for the time being it is hardcoded
        this.setContext(Brand.Uppie, partnerImage , null, null, true, partnerLinkFr, partnerLinkNl);
        setTimeout(() => {
          this.setHelpWidgetSubject(true);
        });
      } else if (this.session.userParam.distributionChannel === DistributionChannel.Broker) {
        if (this.session.userParam.partnercode && brokerLogo) {
          // if partner code exists then left logo = broker and right logo = AG
          this.setContext(Brand.Broker, './assets/images/logo-aginsurance.svg', brokerLogo, brokerWebsiteUrl);
        } else {
          // if partner code is empty or broker image url is not available then left logo = AG and no right logo
          this.setContext(Brand.AG, '');
        }
        setTimeout(() => {
          if (!isFromBrokerInfo){
            this.setHelpWidgetSubject(true); // incase of broker flow widget needs to be shown but not in the broker info screen
          }
        });
      }
    }
    this.saveSession();
  }

  public setSimulationAmounts(oneTime: number, monthly: number): void {
    this.session.simulationData.singleAmount = oneTime;
    this.session.simulationData.monthlyAmount = monthly;
    this.saveSession();
  }

  public setSimulationDuration(years: number): void {
    this.session.simulationData.duration = years;
    this.saveSession();
  }

  public setSimulationData(oneTime: number, monthly: number, years: number): void {
    this.setSimulationAmounts(oneTime, monthly);
    this.setSimulationDuration(years);
  }

  public getSimulationData(): SimulationData {
    return this.session.simulationData;
  }

  public setValidationConstants(
    minAmountOneShot: number,
    minAmountMonthly: number,
    maxAmountOneShot: number,
    maxAmountMonthly: number,
    minDuration: number,
    maxDuration: number
  ): void {
    this.session.ValidationConstants.minAmountOneShot = minAmountOneShot;
    this.session.ValidationConstants.minAmountMonthly = minAmountMonthly;
    this.session.ValidationConstants.maxAmountOneShot = maxAmountOneShot;
    this.session.ValidationConstants.maxAmountMonthly = maxAmountMonthly;
    this.session.ValidationConstants.minDuration = minDuration;
    this.session.ValidationConstants.maxDuration = maxDuration;
    this.saveSession();
  }
  public getValidationConstants(): ValidationConstants {
    return this.session.ValidationConstants;
  }
  public setBeneficiaryChild(beneficiaryChild : BeneficiaryChild  ): void {
    this.session.beneficiaryChild = beneficiaryChild;
    this.saveSession();
  }
  public getBeneficiaryChild(): BeneficiaryChild {
    return this.session.beneficiaryChild;
  }
  public clearBeneficiaryChild(): void{
    this.session.beneficiaryChild = null;
    this.saveSession();
  }

  public setBrokerDetails(brokerData: BrokerInfo, isBrokerKnown?: boolean): void {
    this.session.BrokerInfo = btoa(JSON.stringify(brokerData));
    this.session.isKnownBrokerFlow = isBrokerKnown ? true : false;
    // broker flow would be known, only when user is new and introduced the broker guid in the url and got the data
    this.saveSession();
  }

  public getBrokerDetails(): BrokerInfo {
    return this.session.BrokerInfo ? JSON.parse(atob(this.session.BrokerInfo)) as BrokerInfo : null;
  }

  public get isKnownBrokerFlow(): boolean{
    return this.session.isKnownBrokerFlow;
  }

  public setDeeplinkParams(deeplink: DeepLinkingModel): void{
    this.session.deeplinkParams = deeplink;
    this.saveSession();
  }

  public getDeeplinkParams(): DeepLinkingModel {
    return this.session.deeplinkParams;
  }

  public clearDeeplinkParams(): void {
    this.session.deeplinkParams = null;
    this.saveSession();
  }

  public setClientName(clientName: string): void {
    this.session.clientName = clientName;
    this.saveSession();
  }

  public getChildDetails(): CaptureChildDetails[] {
    return this.session.childDetails;
  }
  public setChildDetails(childData: CaptureChildDetails[]): void {
    this.session.childDetails = childData;
    this.saveSession();
  }
  public clearChildDetails(): void {
    this.session.childDetails = [];
    this.saveSession();
  }
  public getClientName(): string {
    return this.session.clientName;
  }

  public setPartnerInfo(info: {IsPlanDistributor: boolean, Partner: string}): void{
    this.session.PartnerInfo = info;
    this.saveSession();
  }

  public getPartnerInfo(): {IsPlanDistributor: boolean, Partner: string}{
    return this.session.PartnerInfo;
  }

  private prefillSimulationData(date: string, duration: number, oneShortAmount: number, monthlyAmount: number): void {
    if (this.helper.checkIfRecentDate(date)){
      this.setSimulationAmounts(oneShortAmount, monthlyAmount);
      this.setSimulationDuration(duration);
    }
  }

  public setHelpWidgetSubject(flag: boolean): void {
    this.isHelpWidgetRequired.next(flag);
  }

  public getHelpWidgetSubject(): Observable<boolean> {
    return this.isHelpWidgetRequired.asObservable();
  }

  public setUserAuthenticationStatus(flag: boolean): void {
    this.isAuthenticated.next(flag);
  }

  public getUserAuthenticationStatus(): Observable<boolean> {
    return this.isAuthenticated.asObservable();
  }

  public refreshHeaderData(flag: boolean): void {
    this.refreshDynamicHeader.next(flag);
  }

  public hasHeaderRefreshed(): Observable<boolean> {
    return this.refreshDynamicHeader.asObservable();
  }

  public refreshParams(flag: boolean): void {
    this.paramsLoaded.next(flag);
  }

  public hasParamRefreshed(): Observable<boolean> {
    return this.paramsLoaded.asObservable();
  }

  public getAuthConfig(): AuthenticationConfig {
    const authenticationConfig: AuthenticationConfig = {
      clientId: AgAppConfigService.get<string>('MSA_clientId'),
      issuer: AgAppConfigService.get<string>('issuer'),
      loginUrl: AgAppConfigService.get<string>('loginUrl'),
      redirectUri: AgAppConfigService.get<string>('redirectUri'),
      scope: AgAppConfigService.get<string>('Scopes'),
      silentRefreshRedirectUri: AgAppConfigService.get<string>('silentrenewurl'),
      showDebugInformation: true
    }
    return authenticationConfig;
  }

  public authenticate(preserveState?: boolean, currentRoute?: string): Promise<boolean> {
    this.getAuthConfig();
    const additionalStateData: Record<string, string | undefined> = {
      "ProcessCd": localStorage.getItem('ProcessCd'),
      ...(currentRoute ? { PreservedRoutePath: currentRoute } : {})
    }
    const authenticationConfig = this.addQueryParams(this.getAuthConfig());
    const shouldPreserveState = preserveState ?? false;
    return this.authenticationService.login(authenticationConfig, shouldPreserveState, additionalStateData);
  }
  
  addQueryParams(authenticationConfig: AuthenticationConfig) {
    const IsDirectLogin = localStorage.getItem('isDirectLogin');
    const isDirectLogin = IsDirectLogin === 'true';
    const redirectUri = AgAppConfigService.get<string>('redirectUri');
    const language = this.getUserParams()?.language ?  this.getUserParams().language : 'nl';
      if(isDirectLogin) {
        authenticationConfig.customQueryParams = {
          acr_values: `targeturl:${redirectUri}?lang=${language}&forceLogin=true&AllowRegistrationProcess=false`
        }
      } else {
        authenticationConfig.customQueryParams = {
          acr_values: `targeturl:${redirectUri}?lang=${language}&register=true`
        }
      }
     return authenticationConfig;
  }
  public setResourceDetails(resource: ResourceModel[]):void {
    this.session.resource = resource;
    this.saveSession();
  }
  public getResourceDetails(): ResourceModel[] {
    return this.session.resource;
  }
  filterResource(name: string, resource: ResourceModel[]): ResourceModel {
    const filteredData: ResourceModel[] = resource.filter(resObj => resObj.Name === name);
    if (filteredData && filteredData.length) {
      return filteredData[0];
    }
    return null;
  }

  public setPromoCode(validPromoDetails : PromoValidatedData  ): void {
    this.session.validPromoDetails = validPromoDetails;
    this.saveSession();
  }
  public getPromoCode(): PromoValidatedData {
    return this.session.validPromoDetails;
  }
  public clearPromoCode(): void{
    this.session.validPromoDetails = null;
    this.saveSession();
  }

  public setOnlinePaymentInfo(OnlinePaymentSession: Params): void {
    const getSessionValue = this.getOnlinePaymentInfo();
    const onlinePaymentKeys = {
      entryPoint: getSessionValue?.entryPoint ?
        getSessionValue?.entryPoint : OnlinePaymentSession.entryPoint as OnlinePaymentEntryPage,
      amount: getSessionValue?.amount ?
        getSessionValue?.amount : OnlinePaymentSession.amount as number,
      IsRedirectedToUPScreen: getSessionValue?.IsRedirectedToUPScreen ?
        getSessionValue?.IsRedirectedToUPScreen : OnlinePaymentSession.IsRedirectedToUPScreen as boolean,
      isPaymentStatusCalled: getSessionValue?.isPaymentStatusCalled ?
        getSessionValue?.isPaymentStatusCalled : OnlinePaymentSession.isPaymentStatusCalled as boolean,
      onlinePaymentID: getSessionValue?.onlinePaymentID ?
        getSessionValue?.onlinePaymentID : OnlinePaymentSession.onlinePaymentID as string,
      onlinePaymentCODBGUID: getSessionValue?.onlinePaymentCODBGUID ?
        getSessionValue?.onlinePaymentCODBGUID : OnlinePaymentSession.onlinePaymentCODBGUID as string,
      baseURL: getSessionValue?.baseURL ?
        getSessionValue?.baseURL : OnlinePaymentSession.baseURL as string,
        planId : getSessionValue?.planId ? getSessionValue?.planId : OnlinePaymentSession.planId as number
    };
    this.session.OPCrypticInfo = btoa(JSON.stringify(onlinePaymentKeys));
    this.saveSession();
  }

  public getOnlinePaymentInfo(): OnlinePaymentInfo {
    return this.session.OPCrypticInfo ? JSON.parse(atob(this.session.OPCrypticInfo)) as OnlinePaymentInfo : null;
  }

  public clearOnlinePaymentInfo(): void{
    this.session.OPCrypticInfo = null;
    this.saveSession();
  }

  public updateProcessCd(processCd?:string): void {
    const isDirectLogin = localStorage.getItem('isDirectLogin');
    if(processCd) {
      localStorage.setItem('ProcessCd', processCd);
    } 
    else if (this.getUserParams().ProcessCd){  
      localStorage.setItem('ProcessCd', this.getUserParams().ProcessCd);
    } 
    else if(isDirectLogin === 'true') {
      localStorage.setItem('ProcessCd', ProcessCd.DirectLogin);
    }    
    else {
      if(this.getUserParams().distributionChannel === DistributionChannel.Broker){
        localStorage.setItem('ProcessCd', ProcessCd.BrokerFlow);
      } else {
        localStorage.setItem('ProcessCd', ProcessCd.SubscriptionFlow);
      }
    }
  }
}
